import type {
  Destination,
  EventConflict,
  FreeWindow,
  ParsedEventDraft,
  PlannerEvent,
  ScoredDeal,
  TravelDeal,
} from '../types/family-planner'
import { MEMBERS, ymd } from './family-planner-data'

// ─── Date helpers ────────────────────────────────────────────
// Events store naive local strings ('2026-09-24T15:00' or '2026-09-24'), so
// parsing goes through here — `new Date('2026-09-24')` would be read as UTC and
// shift an all-day event onto the wrong day west of Greenwich.

export function parseLocal(iso: string): Date {
  const [date, time] = iso.split('T')
  const [y, m, d] = date.split('-').map(Number)
  if (!time) return new Date(y, m - 1, d)
  const [hh, mm] = time.split(':').map(Number)
  return new Date(y, m - 1, d, hh, mm)
}

export function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

export function dayIndex(iso: string): number {
  return Math.floor(startOfDay(parseLocal(iso)).getTime() / 86400000)
}

export function todayIndex(): number {
  return Math.floor(startOfDay(new Date()).getTime() / 86400000)
}

export function fmtDay(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

export function fmtTime(iso: string): string {
  const d = parseLocal(iso)
  return d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(':00', '')
    .toLowerCase()
}

export function fmtEventTime(e: PlannerEvent): string {
  if (e.allDay) return 'All day'
  return `${fmtTime(e.start)} – ${fmtTime(e.end)}`
}

export function fmtDateRange(startIso: string, endIso: string): string {
  const opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }
  const left = parseLocal(startIso).toLocaleDateString('en-US', opts)
  const right = parseLocal(endIso).toLocaleDateString('en-US', opts)
  return `${left} – ${right}`
}

/** Inclusive day span an event occupies: all-day ends are exclusive. */
export function eventDaySpan(e: PlannerEvent): [number, number] {
  const first = dayIndex(e.start)
  const last = e.allDay ? Math.max(first, dayIndex(e.end) - 1) : dayIndex(e.end)
  return [first, last]
}

// ─── Cross-calendar merge ────────────────────────────────────
// The same meeting invited to two linked accounts arrives twice. Collapse
// copies that share a normalized title and start within 15 minutes, keeping the
// richest copy and unioning the member tags.

function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function mergeDuplicates(events: PlannerEvent[]): PlannerEvent[] {
  const sorted = [...events].sort((a, b) => a.start.localeCompare(b.start))
  const kept: PlannerEvent[] = []

  for (const event of sorted) {
    const key = normalizeTitle(event.title)
    const startMs = parseLocal(event.start).getTime()
    const match = kept.find(
      (k) =>
        normalizeTitle(k.title) === key &&
        k.allDay === event.allDay &&
        Math.abs(parseLocal(k.start).getTime() - startMs) <= 15 * 60000,
    )
    if (!match) {
      kept.push({ ...event })
      continue
    }
    match.mergedFrom = [...(match.mergedFrom ?? []), event.id]
    match.memberIds = [...new Set([...match.memberIds, ...event.memberIds])]
    match.location = match.location || event.location
    match.organizer = match.organizer || event.organizer
    if (event.attendees.length > match.attendees.length) match.attendees = event.attendees
  }
  return kept
}

/** Does the calendar already hold this event? Used to flag inbox duplicates. */
export function findDuplicateEvent(
  draft: { title: string; start: string; allDay: boolean },
  events: PlannerEvent[],
): PlannerEvent | null {
  const key = normalizeTitle(draft.title)
  const startMs = parseLocal(draft.start).getTime()
  return (
    events.find(
      (e) =>
        e.allDay === draft.allDay &&
        Math.abs(parseLocal(e.start).getTime() - startMs) <= 60 * 60000 &&
        (normalizeTitle(e.title) === key ||
          normalizeTitle(e.title).includes(key) ||
          key.includes(normalizeTitle(e.title))),
    ) ?? null
  )
}

// ─── Conflicts ───────────────────────────────────────────────

export function detectConflicts(events: PlannerEvent[]): EventConflict[] {
  const conflicts: EventConflict[] = []
  const timed = events.filter((e) => !e.allDay)

  for (const member of MEMBERS) {
    const mine = timed
      .filter((e) => e.memberIds.includes(member.id))
      .sort((a, b) => a.start.localeCompare(b.start))
    for (let i = 0; i < mine.length; i++) {
      for (let j = i + 1; j < mine.length; j++) {
        const a = mine[i]
        const b = mine[j]
        const aEnd = parseLocal(a.end).getTime()
        const bStart = parseLocal(b.start).getTime()
        if (bStart >= aEnd) break // sorted — nothing later can overlap `a`
        const overlap = Math.round((Math.min(aEnd, parseLocal(b.end).getTime()) - bStart) / 60000)
        if (overlap > 0) {
          conflicts.push({ id: `${a.id}|${b.id}|${member.id}`, memberId: member.id, a, b, overlapMinutes: overlap })
        }
      }
    }
  }
  return conflicts.sort((x, y) => x.a.start.localeCompare(y.a.start))
}

// ─── Free windows ────────────────────────────────────────────

export interface WindowOptions {
  minNights: number
  horizonDays: number
  /** How many members may have something scheduled and still count as a window */
  tolerance?: number
  /** Runs longer than this are reported truncated — past a couple of weeks the
   *  calendar is empty because nothing is booked yet, not because the family is
   *  actually free for a month. */
  maxNights?: number
}

/**
 * Maximal runs of days nobody (or almost nobody) has anything scheduled. This
 * is what the travel tab matches deals against — a deal that lands inside a
 * fully-free run needs no rescheduling at all.
 */
export function findFreeWindows(events: PlannerEvent[], opts: WindowOptions): FreeWindow[] {
  const { minNights, horizonDays, tolerance = 1, maxNights = 14 } = opts
  const start = todayIndex() + 1
  const end = start + horizonDays

  const busyByDay = new Map<number, Set<string>>()
  for (const e of events) {
    const [first, last] = eventDaySpan(e)
    for (let d = Math.max(first, start); d <= Math.min(last, end); d++) {
      const set = busyByDay.get(d) ?? new Set<string>()
      e.memberIds.forEach((m) => set.add(m))
      busyByDay.set(d, set)
    }
  }

  const windows: FreeWindow[] = []
  let runStart: number | null = null
  let runBusy = new Set<string>()

  const flush = (endDay: number) => {
    if (runStart === null) return
    const nights = Math.min(endDay - runStart, maxNights)
    if (nights >= minNights) {
      const startDate = new Date()
      startDate.setHours(0, 0, 0, 0)
      startDate.setDate(startDate.getDate() + (runStart - todayIndex()))
      const endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + nights)
      const busy = [...runBusy]
      windows.push({
        id: `win-${ymd(startDate)}-${nights}`,
        start: ymd(startDate),
        end: ymd(endDate),
        nights,
        freeMemberIds: MEMBERS.map((m) => m.id).filter((m) => !busy.includes(m)),
        busyMemberIds: busy,
        label: `${nights} night${nights === 1 ? '' : 's'}`,
      })
    }
    runStart = null
    runBusy = new Set()
  }

  for (let d = start; d <= end; d++) {
    const busy = busyByDay.get(d) ?? new Set<string>()
    const candidateBusy = new Set([...runBusy, ...busy])
    if (candidateBusy.size <= tolerance) {
      if (runStart === null) runStart = d
      runBusy = candidateBusy
    } else {
      flush(d - 1)
      // The day itself may still start a new run.
      if (busy.size <= tolerance) {
        runStart = d
        runBusy = new Set(busy)
      }
    }
  }
  flush(end)

  return windows.sort((a, b) => {
    const freeDiff = b.freeMemberIds.length - a.freeMemberIds.length
    return freeDiff !== 0 ? freeDiff : b.nights - a.nights
  })
}

// ─── Deal scoring ────────────────────────────────────────────

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v))
}

export function scoreDeals(
  deals: TravelDeal[],
  destinations: Destination[],
  windows: FreeWindow[],
  events: PlannerEvent[],
): ScoredDeal[] {
  const byId = new Map(destinations.map((d) => [d.id, d]))
  const today = todayIndex()

  return deals
    .filter((deal) => {
      const dest = byId.get(deal.destinationId)
      return !!dest && dest.enabled && dayIndex(deal.departDate) > today
    })
    .map((deal) => {
      const dest = byId.get(deal.destinationId)!
      const departDay = dayIndex(deal.departDate)
      const returnDay = dayIndex(deal.returnDate)

      const window =
        windows.find((w) => dayIndex(w.start) <= departDay && dayIndex(w.end) >= returnDay) ?? null

      const blockingEvents = events.filter((e) => {
        const [first, last] = eventDaySpan(e)
        return last >= departDay && first <= returnDay
      })

      const discount = deal.typicalPrice > 0 ? (deal.typicalPrice - deal.price) / deal.typicalPrice : 0
      const withinBudget = deal.price <= dest.maxBudget
      const nightsOk = deal.nights >= dest.minNights && deal.nights <= dest.maxNights

      const calendarFit = window
        ? window.busyMemberIds.length === 0
          ? 1
          : 0.75
        : clamp01(1 - blockingEvents.length * 0.3)
      const budgetFit = withinBudget
        ? 1
        : clamp01(1 - (deal.price - dest.maxBudget) / Math.max(dest.maxBudget, 1))
      const score = Math.round(
        100 *
          (0.45 * clamp01(discount / 0.35) +
            0.3 * calendarFit +
            0.15 * budgetFit +
            0.1 * (nightsOk ? 1 : 0)),
      )

      return {
        ...deal,
        discount,
        score,
        windowId: window?.id ?? null,
        blockingEvents,
        withinBudget,
      }
    })
    .sort((a, b) => b.score - a.score)
}

// ─── Free-text event parsing (WhatsApp / pasted invites) ─────

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
]

function nextWeekday(dow: number, allowToday: boolean): Date {
  const base = startOfDay(new Date())
  let delta = (dow - base.getDay() + 7) % 7
  if (delta === 0 && !allowToday) delta = 7
  base.setDate(base.getDate() + delta)
  return base
}

interface ParseResult {
  draft: ParsedEventDraft
  confidence: number
}

/**
 * Pulls a date, time, title, and location out of a chat message or pasted
 * invite. Deliberately conservative: anything it is unsure about lands in the
 * review inbox with a low confidence score rather than on the calendar.
 */
export function parseEventText(
  text: string,
  defaults: { memberIds: string[]; category: ParsedEventDraft['category'] },
): ParseResult | null {
  const raw = text.trim()
  if (!raw) return null
  const lower = raw.toLowerCase()

  let confidence = 0.3
  let date: Date | null = null
  let dateMatchIndex = raw.length

  const noteDate = (d: Date, index: number, weight: number) => {
    if (date) return
    date = d
    dateMatchIndex = index
    confidence += weight
  }

  // "August 23", "Aug 23rd"
  const monthRe = new RegExp(`\\b(${MONTHS.map((m) => `${m.slice(0, 3)}[a-z]*`).join('|')})\\.?\\s+(\\d{1,2})\\b`, 'i')
  const monthMatch = lower.match(monthRe)
  if (monthMatch) {
    const monthIdx = MONTHS.findIndex((m) => m.startsWith(monthMatch[1].slice(0, 3).toLowerCase()))
    const day = Number(monthMatch[2])
    const now = new Date()
    let year = now.getFullYear()
    const candidate = new Date(year, monthIdx, day)
    if (candidate.getTime() < startOfDay(now).getTime() - 3 * 86400000) year += 1
    noteDate(new Date(year, monthIdx, day), monthMatch.index ?? 0, 0.3)
  }

  // "8/23" or "8/23/26"
  const slashMatch = lower.match(/\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/)
  if (slashMatch) {
    const month = Number(slashMatch[1]) - 1
    const day = Number(slashMatch[2])
    const yearRaw = slashMatch[3] ? Number(slashMatch[3]) : new Date().getFullYear()
    const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw
    noteDate(new Date(year, month, day), slashMatch.index ?? 0, 0.3)
  }

  if (/\btomorrow\b/.test(lower)) {
    const d = startOfDay(new Date())
    d.setDate(d.getDate() + 1)
    noteDate(d, lower.indexOf('tomorrow'), 0.3)
  }
  if (/\btoday\b|\btonight\b/.test(lower)) {
    noteDate(startOfDay(new Date()), Math.max(lower.indexOf('today'), lower.indexOf('tonight')), 0.25)
  }

  // "Saturday", "next Friday"
  for (let i = 0; i < WEEKDAYS.length; i++) {
    const dayRe = new RegExp(`\\b(next\\s+)?${WEEKDAYS[i]}|\\b(next\\s+)?${WEEKDAYS[i].slice(0, 3)}\\b`, 'i')
    const m = lower.match(dayRe)
    if (m) {
      const isNext = /next/.test(m[0])
      const d = nextWeekday(i, false)
      if (isNext) d.setDate(d.getDate() + 7)
      noteDate(d, m.index ?? 0, 0.25)
      break
    }
  }

  // Times: "6:30pm", "6 pm", "18:30", and ranges "6-8pm" / "6 to 8pm"
  const timeRe = /\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/gi
  const times: { hour: number; minute: number; index: number; explicit: boolean }[] = []
  let match: RegExpExecArray | null
  while ((match = timeRe.exec(raw)) !== null) {
    const hourRaw = Number(match[1])
    const minute = match[2] ? Number(match[2]) : 0
    const meridiem = match[3]?.toLowerCase()
    const hasColon = !!match[2]
    if (!meridiem && !hasColon) continue // bare number — probably a date or count
    if (hourRaw > 23) continue
    let hour = hourRaw
    if (meridiem === 'pm' && hour < 12) hour += 12
    if (meridiem === 'am' && hour === 12) hour = 0
    if (!meridiem && hour < 7) hour += 12 // "at 6:30" in a family chat means evening
    times.push({ hour, minute, index: match.index, explicit: !!meridiem })
  }

  // "at 4" / "at 6" with no meridiem or colon still reads as a time.
  if (times.length === 0) {
    const loose = raw.match(/\bat\s+(\d{1,2})\b/i)
    if (loose) {
      let hour = Number(loose[1])
      if (hour <= 7) hour += 12
      times.push({ hour, minute: 0, index: loose.index ?? 0, explicit: false })
    }
  }

  const hasTime = times.length > 0
  if (hasTime) confidence += times[0].explicit ? 0.25 : 0.15
  if (!date && hasTime) {
    // A time with no date means "today" if it hasn't passed, else tomorrow.
    const d = startOfDay(new Date())
    if (times[0].hour * 60 + times[0].minute < new Date().getHours() * 60 + new Date().getMinutes()) {
      d.setDate(d.getDate() + 1)
    }
    noteDate(d, raw.length, 0)
  }
  if (!date) return null

  const day: Date = date
  const startDate = new Date(day)
  if (hasTime) startDate.setHours(times[0].hour, times[0].minute, 0, 0)
  const endDate = new Date(startDate)
  if (hasTime && times.length > 1 && times[1].index - times[0].index < 24) {
    endDate.setHours(times[1].hour, times[1].minute, 0, 0)
    if (endDate <= startDate) endDate.setHours(endDate.getHours() + 12)
  } else {
    endDate.setMinutes(endDate.getMinutes() + 90)
  }

  const fmt = (d: Date) =>
    `${ymd(d)}T${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}`

  // Location: "at Lions Park", "@ the Reynolds'"
  let location: string | undefined
  const locMatch = raw.match(/\b(?:at|@)\s+((?:the\s+)?[A-Z][\w'’.-]*(?:\s+[A-Z0-9][\w'’.-]*){0,4})/)
  if (locMatch && !/^\d/.test(locMatch[1])) {
    location = locMatch[1].trim().replace(/[.,!]$/, '')
    confidence += 0.1
  }

  // Title: the text before the first date/time mention, else the opening words.
  const cut = Math.min(dateMatchIndex, hasTime ? times[0].index : raw.length)
  let title = raw
    .slice(0, cut)
    .replace(/\s*(is|will be|happening|scheduled)?\s*(on|at|for|this|next)?\s*$/i, '')
    .replace(/[-–—:,!]\s*$/, '')
    .trim()
  if (title.length < 3) {
    title = raw.split(/[.!?\n]/)[0].trim()
  }
  title = title.replace(/\s+/g, ' ').slice(0, 80)
  if (title.length < 3) title = 'Untitled event'
  else confidence += 0.05

  return {
    draft: {
      title: title.charAt(0).toUpperCase() + title.slice(1),
      start: hasTime ? fmt(startDate) : ymd(startDate),
      end: hasTime ? fmt(endDate) : ymd(new Date(startDate.getTime() + 86400000)),
      allDay: !hasTime,
      location,
      notes: raw.length > 160 ? `${raw.slice(0, 160)}…` : raw,
      attendees: [],
      memberIds: defaults.memberIds,
      category: defaults.category,
    },
    confidence: Math.min(0.95, Number(confidence.toFixed(2))),
  }
}

// ─── Offline assistant engine ────────────────────────────────
// Runs when no planner API is configured. Same question/answer contract as the
// model-backed endpoint, so the UI is identical either way — answers just come
// from these deterministic rules instead of Claude.

export interface AssistantContext {
  events: PlannerEvent[]
  conflicts: EventConflict[]
  windows: FreeWindow[]
  deals: ScoredDeal[]
}

export interface LocalAnswer {
  text: string
  citations: { label: string; eventId?: string; dealId?: string }[]
  suggestions: string[]
}

function upcoming(events: PlannerEvent[]): PlannerEvent[] {
  const now = new Date().getTime()
  return events
    .filter((e) => parseLocal(e.end).getTime() >= now)
    .sort((a, b) => a.start.localeCompare(b.start))
}

function describe(e: PlannerEvent): string {
  const when = `${fmtDay(parseLocal(e.start))}${e.allDay ? '' : ` at ${fmtTime(e.start)}`}`
  const who = e.memberIds
    .map((id) => MEMBERS.find((m) => m.id === id)?.name ?? id)
    .join(', ')
  return `• **${e.title}** — ${when}${e.location ? ` · ${e.location}` : ''}${who ? ` · ${who}` : ''}`
}

function rangeFor(question: string): { from: number; to: number; label: string } | null {
  const today = todayIndex()
  const dow = new Date().getDay()
  if (/\btoday\b|\btonight\b/.test(question)) return { from: today, to: today, label: 'today' }
  if (/\btomorrow\b/.test(question)) return { from: today + 1, to: today + 1, label: 'tomorrow' }
  if (/\bthis weekend\b|\bthe weekend\b|\bweekend\b/.test(question)) {
    const sat = today + ((6 - dow + 7) % 7)
    return { from: sat, to: sat + 1, label: 'this weekend' }
  }
  if (/\bnext week\b/.test(question)) {
    const nextSun = today + (7 - dow)
    return { from: nextSun, to: nextSun + 6, label: 'next week' }
  }
  if (/\bthis week\b|\bthe week\b/.test(question)) return { from: today, to: today + (6 - dow), label: 'this week' }
  if (/\bthis month\b|\bnext 30\b|\bmonth\b/.test(question)) return { from: today, to: today + 30, label: 'the next 30 days' }
  return null
}

export function answerLocally(question: string, ctx: AssistantContext): LocalAnswer {
  const q = question.toLowerCase().trim()
  const events = upcoming(ctx.events)
  const cite = (list: PlannerEvent[]) => list.slice(0, 6).map((e) => ({ label: e.title, eventId: e.id }))

  const member = MEMBERS.find((m) => q.includes(m.name.toLowerCase()))
  const scoped = member ? events.filter((e) => e.memberIds.includes(member.id)) : events

  // Conflicts
  if (/\bconflict|double.?book|overlap|clash\b/.test(q)) {
    if (ctx.conflicts.length === 0) {
      return {
        text: 'No overlapping events on the aggregated calendar right now.',
        citations: [],
        suggestions: ['What does this week look like?', 'When are we free for 4 nights?'],
      }
    }
    const lines = ctx.conflicts.map((c) => {
      const name = MEMBERS.find((m) => m.id === c.memberId)?.name ?? c.memberId
      return `• **${name}** — "${c.a.title}" and "${c.b.title}" overlap by ${c.overlapMinutes} min on ${fmtDay(parseLocal(c.a.start))}`
    })
    return {
      text: `Found ${ctx.conflicts.length} scheduling conflict${ctx.conflicts.length === 1 ? '' : 's'}:\n\n${lines.join('\n')}`,
      citations: ctx.conflicts.flatMap((c) => [
        { label: c.a.title, eventId: c.a.id },
        { label: c.b.title, eventId: c.b.id },
      ]),
      suggestions: ['Who is free Saturday?', 'Find a getaway window'],
    }
  }

  // Free windows / travel planning
  if (/\bfree\b|\bopen\b|\bgetaway\b|\bvacation\b|\bwindow\b|\bgo away\b|\btime off\b/.test(q)) {
    const nightsMatch = q.match(/(\d+)\s*(night|day)/)
    const need = nightsMatch ? Number(nightsMatch[1]) : 0
    const matches = ctx.windows.filter((w) => w.nights >= need)
    if (matches.length === 0) {
      return {
        text: need
          ? `Nothing open for ${need} straight night${need === 1 ? '' : 's'} inside the planning horizon. The longest clear stretch is ${ctx.windows[0]?.nights ?? 0} nights.`
          : 'No clear multi-night stretches in the planning horizon.',
        citations: [],
        suggestions: ['Show me travel deals', 'What can I move?'],
      }
    }
    const lines = matches.slice(0, 4).map((w) => {
      const busy = w.busyMemberIds
        .map((id) => MEMBERS.find((m) => m.id === id)?.name ?? id)
        .join(', ')
      return `• **${fmtDateRange(w.start, w.end)}** — ${w.nights} nights${busy ? ` · ${busy} has something scheduled` : ' · everyone free'}`
    })
    return {
      text: `${matches.length} open window${matches.length === 1 ? '' : 's'}${need ? ` of ${need}+ nights` : ''}:\n\n${lines.join('\n')}`,
      citations: [],
      suggestions: ['Show me travel deals', 'What is on this weekend?'],
    }
  }

  // Travel deals
  if (/\bdeal|flight|cruise|trip|cheap|fare|price\b/.test(q)) {
    if (ctx.deals.length === 0) {
      return {
        text: 'No live deals are being tracked. Add a destination on the Travel tab and the watcher will start pricing it.',
        citations: [],
        suggestions: ['When are we free for 4 nights?'],
      }
    }
    const top = ctx.deals.slice(0, 3)
    const lines = top.map(
      (d) =>
        `• **${d.title}** — $${d.price.toLocaleString()} (${Math.round(d.discount * 100)}% under typical) · ${fmtDateRange(d.departDate, d.returnDate)}${d.windowId ? ' · fits an open window' : `${d.blockingEvents.length ? ` · conflicts with ${d.blockingEvents.length} event(s)` : ''}`}`,
    )
    return {
      text: `Top ${top.length} tracked deal${top.length === 1 ? '' : 's'} by score:\n\n${lines.join('\n')}`,
      citations: top.map((d) => ({ label: d.title, dealId: d.id })),
      suggestions: ['Which deals fit our calendar?', 'When are we free for 4 nights?'],
    }
  }

  // Date-range agenda
  const range = rangeFor(q)
  if (range) {
    const inRange = scoped.filter((e) => {
      const [first, last] = eventDaySpan(e)
      return last >= range.from && first <= range.to
    })
    const who = member ? `${member.name} has` : 'The family has'
    if (inRange.length === 0) {
      return {
        text: `${who} nothing scheduled ${range.label}.`,
        citations: [],
        suggestions: ['When are we free for 4 nights?', 'Any conflicts coming up?'],
      }
    }
    return {
      text: `${who} ${inRange.length} thing${inRange.length === 1 ? '' : 's'} ${range.label}:\n\n${inRange.map(describe).join('\n')}`,
      citations: cite(inRange),
      suggestions: ['Any conflicts coming up?', 'When are we free for 4 nights?'],
    }
  }

  // Counting
  if (/^how many\b/.test(q)) {
    const horizon = scoped.filter((e) => dayIndex(e.start) <= todayIndex() + 30)
    return {
      text: `${horizon.length} event${horizon.length === 1 ? '' : 's'} in the next 30 days${member ? ` for ${member.name}` : ''}.`,
      citations: cite(horizon),
      suggestions: ['What is on this week?', 'Any conflicts coming up?'],
    }
  }

  // Keyword search across titles, locations, and people
  const stop = new Set([
    'what', 'when', 'where', 'who', 'is', 'are', 'the', 'a', 'an', 'do', 'does', 'we', 'i', 'my',
    'our', 'have', 'has', 'next', 'on', 'for', 'any', 'there', 'in', 'at', 'to', 'up', 'coming',
    'schedule', 'scheduled', 'calendar', 'event', 'events', 'me', 'about', 'tell', 'show', 'list',
  ])
  const terms = q
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 2 && !stop.has(t))
  if (terms.length > 0) {
    const hits = scoped.filter((e) => {
      const hay = `${e.title} ${e.location ?? ''} ${e.category} ${e.notes ?? ''}`.toLowerCase()
      return terms.some((t) => hay.includes(t))
    })
    if (hits.length > 0) {
      const shown = hits.slice(0, 8)
      return {
        text: `${hits.length} match${hits.length === 1 ? '' : 'es'} for "${terms.join(' ')}"${member ? ` on ${member.name}'s schedule` : ''}:\n\n${shown.map(describe).join('\n')}`,
        citations: cite(shown),
        suggestions: ['Any conflicts coming up?', 'When are we free for 4 nights?'],
      }
    }
  }

  const next = scoped.slice(0, 5)
  return {
    text:
      next.length > 0
        ? `Nothing matched that directly. Here is what is coming up next${member ? ` for ${member.name}` : ''}:\n\n${next.map(describe).join('\n')}`
        : 'Nothing is on the aggregated calendar yet — connect a calendar on the Sources tab.',
    citations: cite(next),
    suggestions: ['What is on this weekend?', 'Any conflicts coming up?', 'When are we free for 4 nights?'],
  }
}
